import type {
    CabinetCall,
    CabinetDocument,
    CabinetEmail,
    CabinetLetter,
    CabinetMail,
    CabinetReceivedFax,
    CabinetSentFax,
    FileCabinetRecord,
    FileCabinetViewKey,
} from './file-cabinet.types';

/**
 * What the File Cabinet reads off a record, apart from how it is drawn — shared by the table, the
 * drawer and the nav, and testable under node. Formatting that needs the UI (phone numbers, dates)
 * stays in `ui/file-cabinet`.
 */

/**
 * A value carries a time only when it has one. A date-only string (`2026-10-22`) is a calendar date:
 * shown as is, with no time line — formatting it with time tokens prints the tokens themselves, and
 * reading it as an instant shifts it a day west of UTC. On a received date that drives appeal and
 * timely-filing clocks, a shifted day is a wrong day.
 */
export function hasTime(value: string): boolean {
    return value.includes('T');
}

/**
 * The claims a record is attached to. A document carries them; mail, letters and faxes through their
 * document. Undefined when that is not known — the record came without its document, or the
 * document without `claimIds` — never an empty list standing in for "unknown" (NS-3).
 */
export function claimsOf(view: FileCabinetViewKey, record: FileCabinetRecord): string[] | undefined {
    const document: CabinetDocument | undefined =
        view === 'docs' ? (record as CabinetDocument) : documentOf(record);
    if (!document) {
        return undefined;
    }
    if (document.claimIds?.length) {
        return document.claimIds;
    }
    if (document.claimId) {
        return [document.claimId];
    }
    return document.claimIds ? [] : undefined;
}

/** The document a mail, letter or fax record carries, when the list returned it embedded. */
export function documentOf(record: FileCabinetRecord): CabinetDocument | undefined {
    return (record as CabinetMail | CabinetLetter | CabinetReceivedFax | CabinetSentFax | CabinetEmail | CabinetCall).document;
}

/**
 * A letter's tracking events, newest first — by their time, not the order the API happened to send.
 * Events without a time keep their place after the timed ones.
 */
export function letterEvents(letter: CabinetLetter): NonNullable<NonNullable<CabinetLetter['response']>['trackingEvents']> {
    const events = [...(letter.response?.trackingEvents ?? [])];
    return events
        .map((event, index) => ({ event, index, at: event.time ? Date.parse(event.time) : NaN }))
        .sort((a, b) => {
            const aTimed = !Number.isNaN(a.at);
            const bTimed = !Number.isNaN(b.at);
            if (aTimed && bTimed) {
                return b.at - a.at;
            }
            return aTimed === bTimed ? b.index - a.index : aTimed ? -1 : 1;
        })
        .map(({ event }) => event);
}

/**
 * A letter's last tracking event. Event names are free strings in the API, so they are shown as
 * sent and never coloured as if they meant delivered or returned (NS-4).
 */
export function letterStatus(letter: CabinetLetter): string {
    if (letter.cancelled) {
        return 'Cancelled';
    }
    const [latest] = letterEvents(letter);
    return latest ? latest.name || 'Unnamed event' : 'No events yet';
}

/** The file name a record's download is saved under: the document's own, or the one it carries. */
export function fileCabinetFileName(view: FileCabinetViewKey, record: FileCabinetRecord): string | undefined {
    if (view === 'docs') {
        return (record as CabinetDocument).filename;
    }
    return documentOf(record)?.filename;
}

/** `INBOUND` → `Inbound`; `QUEUE_TRANSFER` → `Queue transfer`. */
export function methodLabel(method?: string): string | undefined {
    if (!method) {
        return undefined;
    }
    // An acronym stays one.
    if (method === 'API') {
        return 'API';
    }
    const words = method.toLowerCase().replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
}

/** `start`–`end` as m:ss. Unknown when either end is missing; said so when they are out of order. */
export function callDuration(start?: string, end?: string): string | undefined {
    if (!start || !end) {
        return undefined;
    }
    const seconds = Math.round((Date.parse(end) - Date.parse(start)) / 1000);
    if (!Number.isFinite(seconds)) {
        return undefined;
    }
    if (seconds < 0) {
        return 'Unknown (ends before it starts)';
    }
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}
